package com.shadowings.gero.presentation.component.quiz

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.pager.PagerState
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.MutableState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawWithContent
import androidx.compose.ui.graphics.rememberGraphicsLayer
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import androidx.navigation.NavController
import androidx.navigation.compose.rememberNavController
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.domain.model.LessonIds
import com.shadowings.gero.domain.model.PhoneticKeyboardContent
import com.shadowings.gero.domain.model.QuizQuestionType
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.domain.model.WrongAnswerRecord
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.HeartActions
import com.shadowings.gero.presentation.model.QuizUiState
import com.shadowings.gero.presentation.screen.QuizScreenContent
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.question_type_transcribe
import gero.composeapp.generated.resources.share_quiz_transcribe
import org.jetbrains.compose.resources.stringResource
import kotlin.random.Random

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
private fun PhoneticSingleSelectionComposablePreview() {
    AppTheme {
        Surface {
            QuizScreenContent(
                uiState = QuizUiState(
                    lessonId = LessonIds.INTRO_HIEROGLYPHS,
                    quizList = listOf(
                        PhoneticKeyboardContent(
                            sentence = "O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33-O34:G43-X1:M33",
                            reading = (0..20).map { "m" },
                            info = "Questo simbolo si legge 'mat' ed è il simbolo per 'madre'."
                        )
                    ),
                    userData = UserData()
                ),
                hiero = listOf(),
                onQuizCompleted = { _, _, _ -> },
                onAddExperience = {},
                heartActions = HeartActions(),
                navController = rememberNavController()
            )
        }
    }
}

@Composable
fun MonoliteralPhoneticQuiz(
    quiz: PhoneticKeyboardContent,
    comboCount: MutableState<Int>,
    quizList: MutableState<List<IQuizContent>>,
    page: Int,
    testMode: Boolean,
    pagerState: PagerState,
    navController: NavController,
    quizResults: MutableState<Map<String, Boolean>>,
    errorsCount: MutableState<Int>,
    userData: UserData,
    lessonId: String = "",
    onComboFiveReached: (() -> Unit)? = null,
    onNextQuestion: ((isCorrect: Boolean) -> Unit)? = null,
    onAnswerResult: ((isCorrect: Boolean, record: WrongAnswerRecord) -> Unit)? = null
) {
    val state = rememberQuizPageState(
        quizId = quiz.id,
        page = page,
        pagerState = pagerState,
        navController = navController,
        quizResults = quizResults,
        quizList = quizList,
        comboCount = comboCount,
        errorsCount = errorsCount,
        onComboFiveReached = onComboFiveReached,
        onNextQuestion = onNextQuestion,
    )
    val graphicsLayer = rememberGraphicsLayer()

    var selectedLetters by rememberSaveable(quiz.id, stateSaver = StringListSaver) {
        mutableStateOf(listOf<String>())
    }
    val rtl = remember { if (quiz.canRotate) Random.nextBoolean() else false }
    val cart = quiz.isProperName
    val variant = remember { quiz.sentence.split("\n").random() }
    val deviceInfo = rememberDeviceInfo()
    val isLandscape = deviceInfo.isLandscape

    Box(
        Modifier.fillMaxSize()
    ) {
        run {
            val questionContent = @Composable {
                Column(
                    modifier = Modifier,
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.SpaceBetween
                ) {
                    val wordBox = @Composable {
                        Box(
                            modifier = Modifier
                                .drawWithContent {
                                    graphicsLayer.record {
                                        this@drawWithContent.drawContent()
                                    }
                                    drawContent()
                                },
                            contentAlignment = Alignment.Center
                        ) {
                            MdCWord(
                                word = variant,
                                rtl = rtl,
                                cart = cart,
                                size = if (isLandscape) 48.dp else 64.dp,
                                testTag = variant
                            )
                        }
                    }
                    val fontScale = LocalDensity.current.fontScale

                    val isLandscapeAndBigFont = isLandscape && fontScale > 1.5f
                    val isLandscapeAndSmallPhone = isLandscape && (
                            deviceInfo.deviceClass == DeviceClass.PHONE_SMALL
                                    || deviceInfo.deviceClass == DeviceClass.PHONE_MEDIUM
                            )

                    val noQuestion = isLandscapeAndSmallPhone || isLandscapeAndBigFont

                    QuestionBox(
                        question = if (noQuestion) "" else stringResource(Res.string.question_type_transcribe),
                        questionTestTag = "mono_phonetic_quiz",
                        wordContent = wordBox
                    )

                    Spacer(Modifier.height(16.dp))

                    PhoneticAnswerSlots(
                        reading = quiz.reading,
                        selectedLetters = selectedLetters,
                        showResult = state.showResult,
                        blinking = state.blinking,
                        isLandscape = isLandscape
                    )
                }
            }

            val keyboardContent = @Composable {
                PhoneticKeyboardInput(
                    reading = quiz.reading,
                    selectedLetters = selectedLetters,
                    onSelectedLettersChange = { selectedLetters = it },
                    enabled = !state.showResult,
                    testMode = testMode,
                    isLandscape = isLandscape
                )
            }

            QuizContentScaffold(
                testTag = "true_false_content",
                isLandscape = isLandscape,
                landscapeOptionsWeight = 1.2f,
                landscapeOptionsArrangement = Arrangement.Center,
                landscapeOptionsHorizontalAlignment = Alignment.CenterHorizontally,
                landscapeOptionsScrollable = false,
                portraitOptionsArrangement = Arrangement.spacedBy(8.dp, Alignment.Bottom),
                questionContent = { questionContent() },
                optionsContent = { keyboardContent() }
            )
        }
        val quizInfo = """
            Quiz ID: ${quiz.id}
            Type: Phonetic Keyboard Quiz
            Sentence/Hieroglyphs: $variant
            Reading (Answer): ${quiz.reading.joinToString("")}
            Info: ${quiz.info}
        """.trimIndent()
        val userAnswer = selectedLetters.joinToString("")

        val shareText = stringResource(Res.string.share_quiz_transcribe)

        QuizBottomFeedbackAndButton(
            modifier = Modifier.align(Alignment.BottomCenter),
            state = state,
            buttonEnabled = selectedLetters.size == quiz.reading.size,
            info = quiz.info,
            correctAnswer = quiz.reading.joinToString(""),
            userData = userData,
            lessonId = lessonId,
            quizInfo = quizInfo,
            userAnswer = userAnswer,
            shareText = shareText,
            shareImageProvider = {
                try {
                    graphicsLayer.toImageBitmap()
                } catch (e: Exception) {
                    null
                }
            },
            onConfirmClick = {
                val isCorrect = selectedLetters.joinToString()
                    .equals(quiz.reading.joinToString(), ignoreCase = true)
                onAnswerResult?.invoke(
                    isCorrect,
                    WrongAnswerRecord(
                        id = WrongAnswerRecord.buildId(lessonId, quiz.sentence),
                        lessonId = lessonId,
                        type = QuizQuestionType.PHONETIC_KEYBOARD,
                        questionText = variant,
                        correctAnswerText = quiz.reading.joinToString(""),
                        userAnswerText = userAnswer,
                        hieroglyphs = variant
                    )
                )
                isCorrect
            }
        )
    }
}
